import { format, addDays, parse, isBefore } from 'date-fns';
import { SERVER_FORMAT_DATE } from '@/service/constants/dates';
import { IDaysAvailability } from '@/domain/Availability.domain';
const dateToNumber = (v: string): number => Number(v.split('-').reverse().join());
const dateSortFunc = (a: string, b: string): number => dateToNumber(a) - dateToNumber(b);

type AvailabilityThumbProps = {
    daysAvailability?: IDaysAvailability;
};

const StatusIcon = ({ type }: { type: 'Today' | 'Tomorrow' | 'Soldout' }) => {
    return <img src={`/icon/${type}.svg`} alt={type} className="mr-1" />;
};

export const AvailabilityThumb = ({ daysAvailability }: AvailabilityThumbProps) => {
    const args = (() => {
        if (
            !daysAvailability ||
            typeof daysAvailability !== 'object' ||
            daysAvailability.length < 1
        ) {
            return null;
        }

        const now = new Date();
        const today = format(now, SERVER_FORMAT_DATE);
        const isAvailToday = !!daysAvailability[today];

        if (isAvailToday) {
            return {
                isAvailToday,
                isAvailTomorrow: false,
                isAvailNearest: false,
                nearestDate: null,
                nearestFormattedDate: '',
                isSoldOut: false,
            };
        }

        const isAvailTomorrow = !!daysAvailability[format(addDays(now, 1), SERVER_FORMAT_DATE)];

        if (isAvailTomorrow) {
            return {
                isAvailToday: false,
                isAvailTomorrow,
                isAvailNearest: false,
                nearestDate: null,
                nearestFormattedDate: '',
                isSoldOut: false,
            };
        }

        const sortedDates = Object.keys(daysAvailability).sort(dateSortFunc);
        const date = sortedDates.find((serverDate) => !!daysAvailability[serverDate]);

        const isAvailNearest = !!date;
        const nearestDate = isAvailNearest ? parse(date, SERVER_FORMAT_DATE, new Date()) : null;
        const thisWeek = isBefore(
            parse(date as string, SERVER_FORMAT_DATE, new Date()),
            addDays(now, 7)
        );

        const nearestFormattedDate =
            isAvailNearest && thisWeek
                ? format(nearestDate as Date, 'iiii')
                : isAvailNearest
                  ? format(nearestDate as Date, 'MMMM do')
                  : '';

        const isSoldOut = !isAvailNearest && !thisWeek;
        return {
            isAvailToday: false,
            isAvailTomorrow: false,
            isAvailNearest,
            nearestDate,
            nearestFormattedDate,
            isSoldOut,
        };
    })();

    if (!args) {
        return <></>;
    }

    const { isAvailToday, isAvailTomorrow, isAvailNearest, nearestFormattedDate, isSoldOut } = args;

    if (!isAvailToday && !isAvailTomorrow && !isAvailNearest && !isSoldOut) {
        return <></>;
    }

    const statusColor =
        ((isAvailToday || isAvailTomorrow) && 'bg-brand') ||
        (isSoldOut && 'bg-state-error') ||
        'bg-gray-5';

    return (
        <div
            data-test-id="availability-thumb"
            className={`${statusColor} tx-large-medium absolute bottom-[-16px] right-3 flex h-9 min-w-[250px] items-end justify-center rounded-[40px] px-3 py-[7px] text-white`}
        >
            {isAvailToday && (
                <>
                    <StatusIcon type="Today" />
                    Experience today
                </>
            )}
            {isAvailTomorrow && (
                <>
                    <StatusIcon type="Tomorrow" />
                    Experience tomorrow
                </>
            )}
            {isAvailNearest && (
                <>
                    <StatusIcon type="Tomorrow" />
                    {` Next Available: ${nearestFormattedDate}`}
                </>
            )}
            {isSoldOut && (
                <>
                    <StatusIcon type="Soldout" />
                    <b>Sold out&nbsp;</b> - try adjusting your dates
                </>
            )}
        </div>
    );
};
